import { useMemo } from 'react';
import { useGlobalStore } from '@atlas/shared-state';

// Exported (ISMC-0059 round 4, JJReviewer CRITICAL-1): every permission hook
// in this file short-circuits on this exact check — it is the platform's
// single canonical "is this the system:*:* wildcard" test. A module-specific
// consumer that needs to know whether the caller is a system admin (e.g. to
// derive its own area set the way this file's hooks derive their own boolean)
// MUST reuse this function rather than write a second wildcard check that can
// drift from it. See `apps/ismc/src/state/use-accessible-modules.ts` for the
// first such consumer.
export function isSystemAdmin(permissions: Array<{ module: string; resource: string; action: string }>): boolean {
  return permissions.some(
    (p) => p.module === 'system' && p.resource === '*' && p.action === '*'
  );
}

export function usePermission(module: string, resource: string, action: string): boolean {
  const permissions = useGlobalStore((state) => state.permissions);
  return useMemo(
    () =>
      isSystemAdmin(permissions) ||
      permissions.some(
        (p) =>
          (p.module === module || p.module === 'system') &&
          (p.resource === resource || p.resource === '*') &&
          (p.action === action || p.action === '*')
      ),
    [permissions, module, resource, action]
  );
}

export function useHasAnyPermission(
  checks: readonly { module: string; resource: string; action: string }[]
): boolean {
  const permissions = useGlobalStore((state) => state.permissions);
  return useMemo(
    () =>
      isSystemAdmin(permissions) ||
      checks.some((check) =>
        permissions.some(
          (p) =>
            (p.module === check.module || p.module === 'system') &&
            (p.resource === check.resource || p.resource === '*') &&
            (p.action === check.action || p.action === '*')
        )
      ),
    [permissions, checks]
  );
}

// Mirrors backend HasAdminScope (PermissionClaimsExtensions.cs:70). Requires
// both `update` AND `delete` (or a covering wildcard) on the resource — the
// gate for "may act on rows that may belong to other users."
export function useHasAdminScope(module: string, resource: string): boolean {
  const permissions = useGlobalStore((state) => state.permissions);
  return useMemo(() => {
    if (isSystemAdmin(permissions)) return true;
    let hasUpdate = false;
    let hasDelete = false;
    for (const p of permissions) {
      if (p.module !== module && p.module !== 'system') continue;
      if (p.resource !== resource && p.resource !== '*') continue;
      if (p.action === '*') return true;
      if (p.action === 'update') hasUpdate = true;
      else if (p.action === 'delete') hasDelete = true;
      if (hasUpdate && hasDelete) return true;
    }
    return false;
  }, [permissions, module, resource]);
}

// Multi-resource variant of useHasAdminScope: true when the user has admin
// scope (update AND delete, or a covering wildcard) on ANY of the listed
// (module, resource) pairs. Reads the permission set once, so it is a single
// hook call — safe to use behind a route guard that ORs several resources
// (e.g. Payroll spans payrollrun/payrollitem/…, Reports spans attendance/
// payrollrun).
export function useHasAnyAdminScope(
  checks: Array<{ module: string; resource: string }>
): boolean {
  const permissions = useGlobalStore((state) => state.permissions);
  return useMemo(() => {
    if (isSystemAdmin(permissions)) return true;
    return checks.some(({ module, resource }) => {
      let hasUpdate = false;
      let hasDelete = false;
      for (const p of permissions) {
        if (p.module !== module && p.module !== 'system') continue;
        if (p.resource !== resource && p.resource !== '*') continue;
        if (p.action === '*') return true;
        if (p.action === 'update') hasUpdate = true;
        else if (p.action === 'delete') hasDelete = true;
        if (hasUpdate && hasDelete) return true;
      }
      return false;
    });
  }, [permissions, checks]);
}

// Most modules use one string for their route/registry ModuleName and their
// permission-catalog `module` key (e.g. Tabang: 'tabang' for both). Item
// Management deliberately does not: its route/registry key is 'item-management'
// (hyphen) while its permissions live under 'itemmanagement' (no hyphen) — a
// documented, intentional split (see the "three spellings" foot-gun notes in
// apps/item-management/.../item-management-roles.constants.ts and the backend
// PermissionSeeder). The nav/route gate is fed the registry ModuleName, so map
// it to the permission-catalog key here; otherwise IM roles that grant
// 'itemmanagement:module:access' fail the 'item-management' gate and the module
// is hidden even from IMAdmin. (IM-0033 / IM-0034.)
const PERMISSION_MODULE_BY_MODULE_NAME: Record<string, string> = {
  'item-management': 'itemmanagement',
};

export function useModuleAccess(module: string): boolean {
  const permissions = useGlobalStore((state) => state.permissions);
  return useMemo(() => {
    const permModule = PERMISSION_MODULE_BY_MODULE_NAME[module] ?? module;
    return (
      isSystemAdmin(permissions) ||
      permissions.some(
        (p) =>
          (p.module === permModule && p.resource === 'module' && p.action === 'access') ||
          (module === 'hr' && p.module === 'hr' && p.resource === 'myess' && p.action === 'access') ||
          (p.module === 'system' && p.resource === '*' && p.action === '*')
      )
    );
  }, [permissions, module]);
}

export function useModuleEnabled(module: string): boolean {
  const enabledModules = useGlobalStore((state) => state.enabledModules);
  return useMemo(() => {
    // If enabledModules hasn't loaded yet (null), treat all as enabled.
    return enabledModules === null || enabledModules.includes(module);
  }, [enabledModules, module]);
}

export function useFieldPermission(
  module: string,
  resource: string,
  field: string,
  action: string
): boolean {
  const permissions = useGlobalStore((state) => state.permissions);
  const fieldPermissions = useGlobalStore((state) => state.fieldPermissions);
  return useMemo(
    () =>
      isSystemAdmin(permissions) ||
      fieldPermissions.some(
        (fp) =>
          fp.module === module &&
          fp.resource === resource &&
          fp.field === field &&
          fp.action === action
      ),
    [permissions, fieldPermissions, module, resource, field, action]
  );
}

export function useFieldPermissions(
  module: string,
  resource: string,
  fields: string[],
  action: string
): Record<string, boolean> {
  const permissions = useGlobalStore((state) => state.permissions);
  const fieldPermissions = useGlobalStore((state) => state.fieldPermissions);
  return useMemo(() => {
    if (isSystemAdmin(permissions)) {
      return Object.fromEntries(fields.map((f) => [f, true]));
    }
    return Object.fromEntries(
      fields.map((f) => [
        f,
        fieldPermissions.some(
          (fp) =>
            fp.module === module &&
            fp.resource === resource &&
            fp.field === f &&
            fp.action === action
        ),
      ])
    );
  }, [permissions, fieldPermissions, module, resource, fields, action]);
}
